import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '资讯', to: '/news' },
  { label: '搜索', to: '/search' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  }

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full bg-primary-950 text-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex h-16 items-center justify-between gap-4 lg:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500 text-lg text-background-50">
              <i className="ri-movie-2-line" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-black tracking-wide">河马AV</span>
              <span className="font-label text-[10px] tracking-[0.28em] text-primary-200">
                HEMA FILM
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            <Link
              to="/"
              className={`rounded-md px-3.5 py-2 text-sm transition-colors ${
                isActive('/') ? 'bg-primary-800 text-background-50' : 'text-primary-100 hover:bg-primary-900'
              }`}
            >
              首页
            </Link>

            <div className="relative" ref={categoryRef}>
              <button
                type="button"
                onClick={() => setCategoryOpen((v) => !v)}
                className={`flex items-center gap-1 rounded-md px-3.5 py-2 text-sm transition-colors ${
                  location.pathname.startsWith('/category')
                    ? 'bg-primary-800 text-background-50'
                    : 'text-primary-100 hover:bg-primary-900'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full mt-2 w-64 animate-fade-in rounded-lg border border-background-200 bg-background-50 p-2 text-foreground-900 shadow-lg">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-primary-100 hover:text-primary-800"
                      >
                        <i className={`${cat.icon} text-primary-500`} />
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3.5 py-2 text-sm transition-colors ${
                  isActive(link.to)
                    ? 'bg-primary-800 text-background-50'
                    : 'text-primary-100 hover:bg-primary-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop search */}
          <form onSubmit={submitSearch} className="hidden max-w-xs flex-1 items-center lg:flex">
            <div className="flex w-full items-center gap-2 rounded-full bg-primary-900 px-4 py-2">
              <i className="ri-search-line text-primary-200" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视作品 / 资讯"
                className="w-full bg-transparent text-sm text-background-50 placeholder:text-primary-300 focus:outline-none"
              />
              <button
                type="submit"
                className="shrink-0 cursor-pointer rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                搜索
              </button>
            </div>
          </form>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开导航菜单"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-2xl text-background-50 lg:hidden"
          >
            <i className={mobileOpen ? 'ri-close-line' : 'ri-menu-line'} />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="animate-fade-in border-t border-primary-900 bg-primary-950 px-4 pb-6 pt-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-4 flex items-center gap-2 rounded-full bg-primary-900 px-4 py-2.5">
            <i className="ri-search-line text-primary-200" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视作品 / 资讯"
              className="w-full bg-transparent text-sm text-background-50 placeholder:text-primary-300 focus:outline-none"
            />
            <button type="submit" className="shrink-0 cursor-pointer rounded-full bg-accent-500 px-3 py-1 text-xs text-background-50">
              搜索
            </button>
          </form>

          <div className="flex flex-col gap-1">
            <Link to="/" className="rounded-md px-3 py-2.5 text-sm text-background-50 hover:bg-primary-900">
              首页
            </Link>
            <p className="px-3 pt-3 pb-1 text-xs tracking-widest text-primary-300">影视分类</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-background-50 hover:bg-primary-900"
                >
                  <i className={`${cat.icon} text-accent-400`} />
                  {cat.name}
                </Link>
              ))}
            </div>
            <p className="px-3 pt-3 pb-1 text-xs tracking-widest text-primary-300">更多</p>
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-3 py-2.5 text-sm text-background-50 hover:bg-primary-900"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}